import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAsyncEffect } from "@hilma/tools";
import Button from "@material-ui/core/Button";
import PublishIcon from "@material-ui/icons/Publish";
import {
  gradeSelect,
  sectorSelect,
  SOCKET_ADMIN_CANDIDATES,
  SOCKET_ADMIN_REMOVE_CANDIDATES,
  teal,
  weekDaySelect,
} from "../../constants";
import {
  CandidateTableTypes,
  SelectDropdown,
  SelectDropdownMenu,
} from "../../types";
import { GenericCandidateTable, StarRating } from "../../componenets";
import { useGetDropdownData } from "../../hooks";
import { useStyles } from "../../componenets/Table/headerButtons/AddButton";
import makeStyles from "@material-ui/core/styles/makeStyles";
import clsx from "clsx";
import axios from "axios";

interface CandidateTablesProps {
  type: CandidateTableTypes;
}

const tableTitle: Record<CandidateTableTypes, string> = {
  [CandidateTableTypes.Approved]: "מחליפים מאושרים",
  [CandidateTableTypes.Pending]: "מחליפים ממתינים לאישור",
  [CandidateTableTypes.Denied]: "מחליפים שלא אושרו",
};

const CandidateTables: React.FC<CandidateTablesProps> = ({ type }) => {
  const [subjectOptions, setSubjectOptions] = useState<SelectDropdownMenu[]>([
    { value: "all", label: "כל המקצועות" },
  ]);

  const [excelData, setExcelData] = useState([]);

  const getDropdownData = useGetDropdownData();
  const classes = useStyles();
  const lClasses = useLocalStyles();

  const navigate = useNavigate();

  const selects: SelectDropdown[] = useMemo(
    () => [
      {
        selectName: "subject",
        defaultValue: "all",
        selectMenu: subjectOptions,
      },
      sectorSelect,
      gradeSelect,
      weekDaySelect,
    ],
    [subjectOptions]
  );

  useAsyncEffect(async () => {
    setSubjectOptions(
      await getDropdownData("/api/subject/get-subjects-select-options")
    );
  }, []);

  const getMoreData = useCallback(
    async (dataUrl) => {
      if (type === CandidateTableTypes.Approved) {
        try {
          const {
            data: { data },
          } = await axios.get(dataUrl, {
            params: { type: CandidateTableTypes.Approved },
          });
          setExcelData(data);
        } catch (error) {
          setExcelData([]);
        }
      }
    },
    [type]
  );

  useEffect(() => {
    getMoreData("/api/candidate/all");
  }, [getMoreData]);

  const handleExport = () => {
    if (excelData.length > 0) {
      const csvData = prepareCSVData(excelData);
      const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "users.csv";
      link.click();
    }
  };

  const prepareCSVData = (data: any) => {
    let csv = "";
    let removeHeaders = ["imagePath", "id"];
    const headers = Object.keys(data[0]).filter(
      (item) => !removeHeaders.includes(item)
    );
    csv += headers.join(",") + "\n";
    data.forEach((row: any) => {
      const values = headers.map((header) => {
        let value = row[header];
        if (value === null || value === undefined) {
          value = "";
        } else if (typeof value === "string" && value.includes(",")) {
          value = `"${value}"`;
        }
        return value;
      });
      csv += values.join(",") + "\n";
    });
    return csv;
  };

  return (
    <>
      <GenericCandidateTable
        additionalHeaderComponents={
          type === CandidateTableTypes.Approved
            ? [
                <Button
                  variant="outlined"
                  color="primary"
                  className={clsx(classes.addButton, lClasses.addExcel)}
                  startIcon={<PublishIcon />}
                  onClick={handleExport}
                  children="ייצוא נתונים לאקסל"
                />,
              ]
            : []
        }
        // additionalColumns={type === CandidateTableTypes.Approved ? [{ field: 'rating', title: 'דירוג', customRender: (candidate, i) => <StarRating numStars={candidate.rating} index={i} /> }] : []} //uncomment for reviews
        shouldRemoveRowOnDeleteSocketEvent={(row, rowsToRemove) =>
          rowsToRemove?.findIndex((r) => r.id === row.id) > -1
        }
        deleteSocketEvent={
          type === CandidateTableTypes.Pending
            ? SOCKET_ADMIN_REMOVE_CANDIDATES
            : undefined
        }
        onAddSocketEventMessage={
          type === CandidateTableTypes.Pending
            ? "נוספו מחליפים שממתינים"
            : undefined
        }
        addSocketEvent={
          type === CandidateTableTypes.Pending
            ? SOCKET_ADMIN_CANDIDATES
            : undefined
        }
        selects={type === CandidateTableTypes.Approved ? selects : undefined}
        arrowOnClick={(row) => navigate(`/candidates/${type}/${row.id}`)}
        showCount={type === CandidateTableTypes.Pending}
        dataUrl={"/api/candidate/all"}
        tableTitle={tableTitle[type]}
        requestData={{ type }}
        wholeRowClick
        type={type}
        search
      />
    </>
  );
};

export default CandidateTables;

const useLocalStyles = makeStyles({
  addExcel: {
    width: "fit-content",
    color: teal,
  },
});
